import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, ShieldCheck, ShieldAlert, RefreshCw, X, CheckCircle2, Circle } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";

const errText = (e, fallback) => e?.response?.data?.detail || fallback;

const maskEmail = (email) => {
  if (!email || !email.includes("@")) return email || "";
  const [local, domain] = email.split("@");
  if (local.length <= 2) return `${local[0] || "•"}•@${domain}`;
  return `${local[0]}${"•".repeat(local.length - 2)}${local[local.length - 1]}@${domain}`;
};

export const EmailManagement = ({ email, recoveryEmail, recoveryVerified, onChanged }) => {
  const [pending, setPending] = useState(null);
  // change-email form
  const [showChange, setShowChange] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [changePw, setChangePw] = useState("");
  const [changing, setChanging] = useState(false);
  // recovery-email form
  const [showRecovery, setShowRecovery] = useState(false);
  const [recEmail, setRecEmail] = useState("");
  const [recPw, setRecPw] = useState("");
  const [savingRec, setSavingRec] = useState(false);
  // remove recovery
  const [showRemove, setShowRemove] = useState(false);
  const [removePw, setRemovePw] = useState("");
  const [removing, setRemoving] = useState(false);
  // lost-current-email recovery flow (Phase 2)
  const [showLostEmail, setShowLostEmail] = useState(false);
  const [recNewEmail, setRecNewEmail] = useState("");
  const [recNewPw, setRecNewPw] = useState("");
  const [submittingRec2, setSubmittingRec2] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/email-change/status");
      setPending(data?.pending ? data : null);
    } catch { /* no-op */ }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const submitChange = async () => {
    if (!newEmail.trim() || !changePw) {
      toast.error("Enter a new email and your current password.");
      return;
    }
    setChanging(true);
    try {
      await api.post("/auth/email-change/request", { new_email: newEmail.trim(), password: changePw });
      toast.success("Confirmation links sent. Check both your current and new inboxes.");
      setShowChange(false); setNewEmail(""); setChangePw("");
      await loadStatus();
    } catch (e) {
      toast.error(errText(e, "Couldn't start the email change."));
    } finally { setChanging(false); }
  };

  const cancelChange = async () => {
    try {
      await api.post("/auth/email-change/cancel");
      toast.success("Email change cancelled.");
      setPending(null);
    } catch (e) {
      toast.error(errText(e, "Couldn't cancel the change."));
    }
  };

  const submitRecoveryRequest = async () => {
    if (!recNewEmail.trim() || !recNewPw) {
      toast.error("Enter a new email and your current password.");
      return;
    }
    setSubmittingRec2(true);
    try {
      await api.post("/auth/email-recovery/request", { new_email: recNewEmail.trim(), password: recNewPw });
      toast.success("Recovery started. Check your new email and your recovery email to confirm.");
      setShowLostEmail(false); setRecNewEmail(""); setRecNewPw("");
      await loadStatus();
    } catch (e) {
      toast.error(errText(e, "Couldn't start account recovery."));
    } finally { setSubmittingRec2(false); }
  };

  const cancelRecovery = async () => {
    try {
      await api.post("/auth/email-recovery/cancel");
      toast.success("Recovery request cancelled.");
      setPending(null);
    } catch (e) {
      toast.error(errText(e, "Couldn't cancel the recovery request."));
    }
  };

  const saveRecovery = async () => {
    if (!recEmail.trim() || !recPw) {
      toast.error("Enter a recovery email and your current password.");
      return;
    }
    setSavingRec(true);
    try {
      await api.post("/auth/recovery-email", { recovery_email: recEmail.trim(), password: recPw });
      toast.success("Verification link sent to your recovery email.");
      setShowRecovery(false); setRecEmail(""); setRecPw("");
      onChanged?.();
    } catch (e) {
      toast.error(errText(e, "Couldn't save the recovery email."));
    } finally { setSavingRec(false); }
  };

  const removeRecovery = async () => {
    if (!removePw) { toast.error("Enter your current password."); return; }
    setRemoving(true);
    try {
      await api.delete("/auth/recovery-email", { data: { password: removePw } });
      toast.success("Recovery email removed.");
      setShowRemove(false); setRemovePw("");
      onChanged?.();
    } catch (e) {
      toast.error(errText(e, "Couldn't remove the recovery email."));
    } finally { setRemoving(false); }
  };

  const onEnter = (e, fn) => { if (e.key === "Enter") { e.preventDefault(); fn(); } };

  return (
    <Card className="p-6" data-testid="email-management-card">
      <h2 className="flex items-center gap-2 text-lg font-[640] text-foreground">
        <Mail className="h-4 w-4" /> Login email
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        This is the email you sign in with. Changing it needs confirmation from both your current and new address.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <code className="rounded-md border border-border bg-secondary/60 px-3 py-2 text-sm text-foreground" data-testid="email-current">
          {email || "—"}
        </code>
        {!pending && (
          <Button type="button" variant="secondary" size="sm" onClick={() => setShowChange((s) => !s)} data-testid="email-change-toggle">
            {showChange ? "Cancel" : "Change email"}
          </Button>
        )}
      </div>

      {/* Pending change panel (primary email change) */}
      {pending && pending.type !== "recovery" && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/60 p-4" data-testid="email-change-pending">
          <p className="text-sm font-[560] text-amber-900">
            Pending change to <span className="font-mono" data-testid="email-change-pending-target">{pending.new_email_masked}</span>
          </p>
          <ul className="mt-3 space-y-1.5 text-sm text-amber-900">
            <li className="flex items-center gap-2" data-testid="email-change-old-status">
              {pending.old_approved ? <CheckCircle2 className="h-4 w-4 text-teal-600" /> : <Circle className="h-4 w-4 text-amber-500" />}
              Approve from your current email
            </li>
            <li className="flex items-center gap-2" data-testid="email-change-new-status">
              {pending.new_verified ? <CheckCircle2 className="h-4 w-4 text-teal-600" /> : <Circle className="h-4 w-4 text-amber-500" />}
              Verify your new email
            </li>
          </ul>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={loadStatus} data-testid="email-change-refresh">
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh
            </Button>
            <Button type="button" variant="destructive" size="sm" onClick={cancelChange} data-testid="email-change-cancel">
              <X className="mr-1.5 h-3.5 w-3.5" /> Cancel change
            </Button>
          </div>
          <p className="mt-3 text-xs text-amber-800">
            Once both are confirmed, your email updates and you'll be signed out everywhere — sign back in with the new address.
          </p>
        </div>
      )}

      {/* Pending recovery panel (lost-current-email recovery) */}
      {pending && pending.type === "recovery" && (
        <div className="mt-4 rounded-lg border border-sky-200 bg-sky-50/60 p-4" data-testid="email-recovery-pending">
          <p className="text-sm font-[560] text-sky-900">
            Recovering access — changing to <span className="font-mono" data-testid="email-recovery-pending-target">{pending.new_email_masked}</span>
          </p>
          <ul className="mt-3 space-y-1.5 text-sm text-sky-900">
            <li className="flex items-center gap-2" data-testid="email-recovery-new-status">
              {pending.new_verified ? <CheckCircle2 className="h-4 w-4 text-teal-600" /> : <Circle className="h-4 w-4 text-sky-500" />}
              Verify your new email
            </li>
            <li className="flex items-center gap-2" data-testid="email-recovery-confirm-status">
              {pending.recovery_confirmed ? <CheckCircle2 className="h-4 w-4 text-teal-600" /> : <Circle className="h-4 w-4 text-sky-500" />}
              Confirm from your recovery email
            </li>
          </ul>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={loadStatus} data-testid="email-recovery-refresh">
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh
            </Button>
            <Button type="button" variant="destructive" size="sm" onClick={cancelRecovery} data-testid="email-recovery-cancel">
              <X className="mr-1.5 h-3.5 w-3.5" /> Cancel recovery
            </Button>
          </div>
          <p className="mt-3 text-xs text-sky-800">
            Your current email has been notified. Once both are confirmed, your email updates and you'll be signed out everywhere.
          </p>
        </div>
      )}

      {/* Change form */}
      {showChange && !pending && (
        <div className="mt-4 space-y-3 rounded-lg border border-border bg-secondary/30 p-4" data-testid="email-change-form">
          <div className="space-y-1.5">
            <Label>New email</Label>
            <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => onEnter(e, submitChange)} placeholder="you@newdomain.com" data-testid="email-change-new-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Current password</Label>
            <Input type="password" value={changePw} onChange={(e) => setChangePw(e.target.value)}
              onKeyDown={(e) => onEnter(e, submitChange)} placeholder="Confirm your password" data-testid="email-change-password-input" />
          </div>
          <Button type="button" onClick={submitChange} disabled={changing} data-testid="email-change-submit">
            {changing ? "Sending…" : "Send confirmation links"}
          </Button>
        </div>
      )}

      {/* Lost access to current email? (Phase 2 recovery) */}
      {!pending && (
        <div className="mt-3" data-testid="lost-email-section">
          {recoveryVerified ? (
            <button type="button" onClick={() => setShowLostEmail((s) => !s)}
              className="text-sm font-[560] text-primary hover:underline" data-testid="lost-email-toggle">
              {showLostEmail ? "Never mind" : "Lost access to your current email?"}
            </button>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="lost-email-blocked">
              Lost access to your current email? Add &amp; verify a recovery email below first, then this option unlocks.
            </p>
          )}
        </div>
      )}

      {showLostEmail && !pending && recoveryVerified && (
        <div className="mt-4 space-y-3 rounded-lg border border-sky-200 bg-sky-50/40 p-4" data-testid="lost-email-form">
          <p className="text-sm text-sky-900">
            We'll email a confirmation link to your <strong>new address</strong> and to your <strong>verified recovery email</strong>. Your current email will be notified. Your login email won't change until both are confirmed.
          </p>
          <div className="space-y-1.5">
            <Label>New login email</Label>
            <Input type="email" value={recNewEmail} onChange={(e) => setRecNewEmail(e.target.value)}
              onKeyDown={(e) => onEnter(e, submitRecoveryRequest)} placeholder="you@newdomain.com" data-testid="lost-email-new-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Current password</Label>
            <Input type="password" value={recNewPw} onChange={(e) => setRecNewPw(e.target.value)}
              onKeyDown={(e) => onEnter(e, submitRecoveryRequest)} placeholder="Confirm your password" data-testid="lost-email-password-input" />
          </div>
          <Button type="button" onClick={submitRecoveryRequest} disabled={submittingRec2} data-testid="lost-email-submit">
            {submittingRec2 ? "Starting…" : "Start account recovery"}
          </Button>
        </div>
      )}

      <Separator className="my-5" />

      {/* Recovery email */}
      <h2 className="flex items-center gap-2 text-lg font-[640] text-foreground">
        <ShieldCheck className="h-4 w-4" /> Recovery email
        <span className="text-sm font-[400] text-muted-foreground">(private)</span>
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        A private, verified backup address. It's never shown on your public profile and isn't used to sign in.
      </p>

      {recoveryEmail ? (
        <div className="mt-4">
          <div className="flex flex-wrap items-center gap-3">
            <code className="rounded-md border border-border bg-secondary/60 px-3 py-2 text-sm text-foreground" data-testid="recovery-email-value">
              {maskEmail(recoveryEmail)}
            </code>
            {recoveryVerified
              ? <Badge className="bg-teal-50 text-teal-700 hover:bg-teal-50" data-testid="recovery-email-verified"><ShieldCheck className="mr-1 h-3 w-3" /> Verified</Badge>
              : <Badge variant="secondary" className="text-amber-700" data-testid="recovery-email-unverified"><ShieldAlert className="mr-1 h-3 w-3" /> Unverified</Badge>}
          </div>
          {!recoveryVerified && (
            <p className="mt-2 text-xs text-amber-700" data-testid="recovery-email-verify-hint">
              Check that inbox for the verification link. You can re-send by setting it again below.
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={() => { setShowRecovery((s) => !s); setShowRemove(false); }} data-testid="recovery-email-change-toggle">
              {showRecovery ? "Cancel" : "Change"}
            </Button>
            <Button type="button" variant="destructive" size="sm" onClick={() => { setShowRemove((s) => !s); setShowRecovery(false); }} data-testid="recovery-email-remove-toggle">
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-4">
          <Button type="button" variant="secondary" size="sm" onClick={() => setShowRecovery((s) => !s)} data-testid="recovery-email-add-toggle">
            {showRecovery ? "Cancel" : "Add recovery email"}
          </Button>
        </div>
      )}

      {showRecovery && (
        <div className="mt-4 space-y-3 rounded-lg border border-border bg-secondary/30 p-4" data-testid="recovery-email-form">
          <div className="space-y-1.5">
            <Label>Recovery email</Label>
            <Input type="email" value={recEmail} onChange={(e) => setRecEmail(e.target.value)}
              onKeyDown={(e) => onEnter(e, saveRecovery)} placeholder="backup@personal.com" data-testid="recovery-email-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Current password</Label>
            <Input type="password" value={recPw} onChange={(e) => setRecPw(e.target.value)}
              onKeyDown={(e) => onEnter(e, saveRecovery)} placeholder="Confirm your password" data-testid="recovery-email-password-input" />
          </div>
          <Button type="button" onClick={saveRecovery} disabled={savingRec} data-testid="recovery-email-submit">
            {savingRec ? "Saving…" : "Send verification link"}
          </Button>
        </div>
      )}

      {showRemove && (
        <div className="mt-4 space-y-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4" data-testid="recovery-email-remove-form">
          <div className="space-y-1.5">
            <Label>Current password</Label>
            <Input type="password" value={removePw} onChange={(e) => setRemovePw(e.target.value)}
              onKeyDown={(e) => onEnter(e, removeRecovery)} placeholder="Confirm your password" data-testid="recovery-email-remove-password-input" />
          </div>
          <Button type="button" variant="destructive" onClick={removeRecovery} disabled={removing} data-testid="recovery-email-remove-submit">
            {removing ? "Removing…" : "Remove recovery email"}
          </Button>
        </div>
      )}
    </Card>
  );
};
